// Follow camera with damping, screen-shake (trauma model), FOV punch, KO dolly.

import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { CONFIG } from '../config';

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  /** PositionalAudio anchor — attached to the camera so 3D pan/attenuation
   *  tracks the follow-cam's world pose. Consumers (e.g. GameAudio) read
   *  this when building PositionalAudio nodes. */
  readonly listener: THREE.AudioListener;
  private targetPos = new THREE.Vector3();
  private currentLook = new THREE.Vector3();
  private trauma = 0;
  private fovPunch = 0;
  private dollyExtra = 0;
  private shakeOffset = new THREE.Vector3();
  // Reused every frame so `update()` doesn't allocate a fresh Vector3 each
  // rAF for the desired-position calculation.
  private _desired = new THREE.Vector3();
  // Rapier world used for camera-occlusion raycasts. Null until the
  // Renderer wires it up post-sim-init; while null, the occlusion check
  // is a no-op and the camera behaves as a pure follow-cam.
  private rapierWorld: RAPIER.World | null = null;
  private _occRay: RAPIER.Ray | null = null;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(CONFIG.camera.fov, aspect, 0.1, 1000);
    this.camera.position.set(0, CONFIG.camera.height, CONFIG.camera.distance);
    this.listener = new THREE.AudioListener();
    this.camera.add(this.listener);
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  addTrauma(t: number): void { this.trauma = Math.min(1, this.trauma + t); }
  addFovPunch(p: number): void { this.fovPunch = Math.min(8, this.fovPunch + p); }
  addDolly(d: number): void { this.dollyExtra = Math.min(2.5, this.dollyExtra + d); }

  setOcclusionWorld(world: RAPIER.World | null): void {
    this.rapierWorld = world;
    if (world && !this._occRay) {
      this._occRay = new RAPIER.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 1 });
    }
  }

  update(focusX: number, focusY: number, focusZ: number, aimYaw: number, aimPitch: number, dt: number, disableFloor = false): void {
    this.targetPos.set(focusX, focusY, focusZ);
    const dist = CONFIG.camera.distance + this.dollyExtra;
    // Spherical orbit around focus. Yaw sweeps horizontally, pitch raises
    // (pitch>0 = camera above, looking down) or lowers the camera.
    // Forward convention matches yawFromQuat: (sin yaw, 0, cos yaw).
    const sin = Math.sin(aimYaw), cos = Math.cos(aimYaw);
    const cp = Math.cos(aimPitch), sp = Math.sin(aimPitch);
    const horiz = dist * cp;
    const desired = this._desired.set(
      focusX - sin * horiz,
      focusY + CONFIG.camera.height + dist * sp,
      focusZ - cos * horiz
    );
    // Safety floor: never let the camera dip below the platform even if a
    // low focus + extreme pitch conspires to push it underground. Swim
    // mode disables the floor so the cam can dip under the water surface
    // and trigger the underwater post / lowpass.
    if (!disableFloor && desired.y < 0.4) desired.y = 0.4;

    // Camera occlusion: cast a ray from focus → desired and, if a static
    // collider is closer than the desired distance, pull the *lerp target*
    // in to `toi - occlusionBuffer`. Clamping the target (not the post-
    // lerp position) keeps the lerp from fighting a per-frame snap-back —
    // the camera glides smoothly into the safe spot and back out when the
    // occluder clears, instead of juddering as the lerp and snap battle.
    if (CONFIG.camera.occlusionEnabled && this.rapierWorld && this._occRay) {
      const dx = desired.x - this.targetPos.x;
      const dy = desired.y - this.targetPos.y;
      const dz = desired.z - this.targetPos.z;
      const ddist = Math.hypot(dx, dy, dz);
      if (ddist > 1e-4) {
        const inv = 1 / ddist;
        const nx = dx * inv, ny = dy * inv, nz = dz * inv;
        this._occRay.origin.x = this.targetPos.x;
        this._occRay.origin.y = this.targetPos.y;
        this._occRay.origin.z = this.targetPos.z;
        this._occRay.dir.x = nx;
        this._occRay.dir.y = ny;
        this._occRay.dir.z = nz;
        // Filter by collision-group membership: world/arena geometry uses
        // bit 0 only (matches the predicate Ragdoll.castArenaRayRaw uses
        // for its ground checks). Skips characters/ropes/dynamic props so
        // the camera only pulls in for actual map walls/floors/ceilings.
        const hit = this.rapierWorld.castRay(
          this._occRay,
          ddist,
          true,
          undefined, undefined, undefined, undefined,
          (c) => {
            const groups = c.collisionGroups();
            const membership = (groups >>> 16) & 0xffff;
            return (membership & ~1) === 0;
          }
        );
        if (hit != null && hit.timeOfImpact < ddist) {
          const safe = Math.max(hit.timeOfImpact - CONFIG.camera.occlusionBuffer, CONFIG.camera.occlusionMinDist);
          desired.set(
            this.targetPos.x + nx * safe,
            this.targetPos.y + ny * safe,
            this.targetPos.z + nz * safe
          );
        }
      }
    }

    this.camera.position.lerp(desired, 1 - Math.pow(1 - CONFIG.camera.followLerp, dt * 60));
    this.currentLook.lerp(this.targetPos, 1 - Math.pow(1 - CONFIG.camera.lookLerp, dt * 60));

    // Trauma → shake
    this.trauma = Math.max(0, this.trauma - dt * 1.6);
    const t2 = this.trauma * this.trauma;
    const tt = performance.now() * 0.06;
    const sx = (Math.sin(tt * 1.7) + Math.sin(tt * 4.1)) * 0.5;
    const sy = (Math.cos(tt * 2.3) + Math.cos(tt * 5.5)) * 0.5;
    const sz = (Math.sin(tt * 3.1) + Math.cos(tt * 6.0)) * 0.5;
    const max = CONFIG.camera.shakeMax;
    this.shakeOffset.set(sx * t2 * max, sy * t2 * max * 0.5, sz * t2 * max * 0.4);

    this.camera.position.add(this.shakeOffset);
    this.camera.lookAt(this.currentLook);

    // FOV punch decays
    this.fovPunch = Math.max(0, this.fovPunch - dt * 14);
    this.camera.fov = CONFIG.camera.fov + this.fovPunch;
    this.camera.updateProjectionMatrix();

    // Dolly decays
    this.dollyExtra = Math.max(0, this.dollyExtra - dt * 1.2);
  }

  reset(): void {
    this.trauma = 0;
    this.fovPunch = 0;
    this.dollyExtra = 0;
  }
}
